import React, { useRef } from 'react';
import { BlankModal, HStack, ModalsRenderer, VStack, Box } from '@cad-arc';
import { EventPanelFooter } from './event-panel-footer/event-panel-footer';
import { EventPanelLeftPane } from './event-panel-left-pane';
import { EventPanelRightPane } from './event-panel-right-pane';
import { useExpandablePanelSize } from '../panels/expandable-panel-size';
import { EventPanelTabs } from './tabs/components/event-panel-tabs';
import { EventPanelModal, useEventPanelModal, useSetEventPanelModal } from './event-panel-modals';
import { ClearEventModal } from './clear-event-modal';
import { UpdateDispositionModal } from './update-disposition-modal';
import { useSetActiveEventPanelTab } from './active-event-panel-tab-provider';
import { RequestAssistanceModal } from '../request-assistance/components/request-assistance-modal';
import { CopyCallModal } from '../copy-call/components/copy-call-modal';

type PortalProps = React.ComponentProps<typeof BlankModal>['portalProps'];
function PanelModal(props: { modal?: EventPanelModal; portalProps: PortalProps }) {
    const setEventPanelModal = useSetEventPanelModal();
    const setActiveEventPanelTab = useSetActiveEventPanelTab();
    const onClose = () => setEventPanelModal(undefined);
    const propsToSpread = {
        onClose,
        portalProps: props.portalProps,
        constrainToParentPortal: true,
        closeOnOverlayClick: false,
    } as const;

    switch (props.modal?.type) {
        case 'UPDATE_DISPOSITION': {
            return <UpdateDispositionModal {...props.modal.props} {...propsToSpread} />;
        }
        case 'CLEAR_EVENT': {
            return (
                <ClearEventModal
                    {...props.modal.props}
                    {...propsToSpread}
                    setActiveEventPanelTab={setActiveEventPanelTab}
                />
            );
        }
        case 'REQUEST_ASSISTANCE': {
            return <RequestAssistanceModal {...props.modal.props} {...propsToSpread} />;
        }
        case 'COPY_CALL': {
            return <CopyCallModal {...props.modal.props} {...propsToSpread} />;
        }
        default: {
            return null;
        }
    }
}

export function EventPanelBody(): React.ReactElement {
    const eventPanelSize = useExpandablePanelSize();
    const eventPanelModal = useEventPanelModal();
    const modalContainerRef = useRef<HTMLDivElement | null>(null);
    return (
        <VStack
            spacing={0}
            h="100%"
            w="100%"
            overflowY="hidden"
            position="relative"
            ref={modalContainerRef}
        >
            <ModalsRenderer scope="EVENT_PANEL_MODALS" portalContainerRef={modalContainerRef}>
                <PanelModal
                    modal={eventPanelModal}
                    portalProps={{ containerRef: modalContainerRef }}
                />
            </ModalsRenderer>
            <HStack spacing={0} overflowY="hidden" justifyContent="flex-start" w="100%" h="100%">
                <EventPanelLeftPane />
                {eventPanelSize === 'XL' ? (
                    <EventPanelRightPane />
                ) : eventPanelSize === 'XXL' ? (
                    <Box w="500px" h="100%" flexGrow={10000}>
                        <EventPanelTabs mode="FULL" />
                    </Box>
                ) : undefined}
                {eventPanelSize === 'XXL' ? <EventPanelRightPane /> : undefined}
            </HStack>
            <EventPanelFooter />
        </VStack>
    );
}
